import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { salesTeamMembers } from "../../lib/sales-teams";
import type { PicklistOption } from "../../lib/crm-types";
import { Badge } from "../ui/Badge";
import { Popover } from "../ui/Popover";
import { cn } from "../ui/cn";

/**
 * The two fields you change by picking, wherever they appear.
 *
 * Lifted out of the company page so the list can use the same controls. They
 * were written for the header, and a second copy in the table would be two
 * places to fix when "Clear" or the empty state needs a word changing -- and
 * two dropdowns that behaved almost the same would be worse than one that
 * behaved consistently.
 */

/** Turns a stored picklist value back into the label a manager chose. */
export function optionLabel(
  options: PicklistOption[] | undefined,
  value: string,
): string {
  return options?.find((option) => option.value === value)?.label ?? value;
}

/**
 * A picklist-backed field, changeable in place.
 *
 * Shown even when empty, as a faint "Set stage". An account with no stage is
 * precisely the one somebody should be able to give a stage to, and hiding the
 * badge left it the only account with nothing to click.
 */
export function PicklistBadge({
  label: name,
  value,
  options,
  onChange,
  pending,
  suffix = "",
  overlay,
}: {
  label: string;
  value: string | null;
  options: PicklistOption[];
  onChange: (value: string | null) => void;
  pending: boolean;
  /** Appended to the chosen label, for "high priority" rather than "high". */
  suffix?: string;
  /** Set inside anything that scrolls -- see Popover's own `overlay`. */
  overlay?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const choose = (next: string | null): void => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      overlay={overlay}
      label={`Change ${name.toLowerCase()}`}
      className="max-h-72 w-48 overflow-y-auto p-1"
      trigger={
        value ? (
          <span className={cn(pending && "opacity-50")}>
            <Badge>
              {optionLabel(options, value)}
              {suffix}
            </Badge>
          </span>
        ) : (
          <span className="rounded border border-dashed border-line px-1.5 py-0.5 text-[11px] text-faint hover:border-brand-300 hover:text-muted">
            Set {name.toLowerCase()}
          </span>
        )
      }
    >
      {/* A manager defines these, and on a new desk nobody has yet. An empty
          box would read as broken; this says whose job it is. */}
      {options.length === 0 ? (
        <p className="px-2 py-1.5 text-[11px] text-faint">
          No {name.toLowerCase()} options yet. A manager sets them up under{" "}
          <Link to="/crm/settings" className="text-brand-600 hover:underline">
            CRM settings
          </Link>
          .
        </p>
      ) : null}

      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => choose(option.value)}
          className={cn(
            "flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-surface-2",
            option.value === value ? "text-brand-600" : "text-content",
          )}
        >
          {option.label}
          {option.value === value ? <span>✓</span> : null}
        </button>
      ))}

      {value ? (
        <button
          type="button"
          onClick={() => choose(null)}
          className="mt-1 w-full rounded border-t border-line px-2 py-1.5 text-left text-xs text-muted hover:bg-surface-2"
        >
          Clear
        </button>
      ) : null}
    </Popover>
  );
}

/**
 * Who manages the account, changeable in place.
 *
 * A dropdown on the name rather than a trip through the Edit dialog: handing an
 * account over is a thing that happens in the middle of a conversation about
 * it, and the name is where somebody is already looking when they decide to.
 *
 * The sales desks only, not every user. Managing an account is a sales job --
 * the at-risk rules are configured per desk and the AM dashboard measures
 * people by the desk they sit on -- so offering the whole directory meant the
 * picker could hand an account to somebody with no book to put it in, and did.
 * Who counts is team membership, set in Settings -> Teams; nothing here is a
 * second place to configure it.
 *
 * Within that, everybody on a desk rather than only those already holding an
 * account -- the whole point is usually to give one to somebody who has none.
 *
 * Takes the id and the name rather than a company, because the list has rows
 * and the page has a detail record, and this needs to work the same on both.
 */
export function OwnerPicker({
  ownerUserId,
  onChange,
  pending,
  triggerLabel,
  triggerClassName,
  overlay,
}: {
  ownerUserId: number | null;
  onChange: (ownerUserId: number | null) => void;
  pending: boolean;
  /** What the trigger reads when nobody is set, or to override the name. */
  triggerLabel: string;
  triggerClassName?: string;
  /** Set inside anything that scrolls -- see Popover's own `overlay`. */
  overlay?: boolean;
}) {
  const [open, setOpen] = useState(false);

  /*
   * Two requests rather than one, because neither answers on its own: the team
   * list carries who is on a sales desk, and it is the only list that does.
   * Both are shared cache keys the settings pages already fill, so opening this
   * twice in a session costs nothing, and neither is fetched until somebody
   * opens the menu -- most visits never change the account manager.
   */
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const teams = useQuery({
    queryKey: queryKeys.teams,
    queryFn: api.teams,
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const candidates = useMemo(
    () => salesTeamMembers(teams.data ?? [], users.data ?? []),
    [teams.data, users.data],
  );

  const choose = (next: number | null): void => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      overlay={overlay}
      label="Change who manages this account"
      className="max-h-72 w-56 overflow-y-auto p-1"
      trigger={
        <span
          title="Change who manages this account"
          className={cn(
            "rounded px-1 py-0.5 underline decoration-dotted underline-offset-2 hover:bg-surface hover:text-brand-600",
            pending && "opacity-50",
            triggerClassName ?? "text-content",
          )}
        >
          {triggerLabel}
        </span>
      }
    >
      {users.isLoading || teams.isLoading ? (
        <p className="px-2 py-1.5 text-[11px] text-faint">Loading…</p>
      ) : (
        <>
          {/*
            Said when it applies, and it applies on a desk that has created the
            sales teams and not put anybody in them -- which is every desk on
            the day it starts. Without this the picker is indistinguishable
            from the one that offered everybody by design.
          */}
          {candidates.everybody ? (
            <p className="px-2 py-1.5 text-[11px] text-faint">
              Nobody is on a sales desk yet, so everybody is listed. Settings →
              Teams decides who manages accounts.
            </p>
          ) : null}

          {candidates.people.map((person) => (
            <button
              key={person.id}
              type="button"
              onClick={() => choose(person.id)}
              className={cn(
                "flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-surface-2",
                person.id === ownerUserId ? "text-brand-600" : "text-content",
              )}
            >
              {person.name}
              {person.id === ownerUserId ? <span>✓</span> : null}
            </button>
          ))}

          {/* Handing an account back is as real as handing it over, and it is
              what puts the account into the "unmanaged" queue somebody works. */}
          {ownerUserId !== null ? (
            <button
              type="button"
              onClick={() => choose(null)}
              className="mt-1 w-full rounded border-t border-line px-2 py-1.5 text-left text-xs text-muted hover:bg-surface-2"
            >
              Unassigned
            </button>
          ) : null}
        </>
      )}
    </Popover>
  );
}
